<template>
  <router-link
    :class="[
      'product-thumbnail__link',
      {
        'product-thumbnail__link--horizontal': horizontalCard,
        'product-thumbnail__link--bestoffer': bestOffer || isAuthenticated,
        'product-thumbnail__link--fixedWidth': fixedWidth,
      },
    ]"
    data-test-catalog-product-thumbnail
    :to="{ name: 'product', params: { productSlug: productSlug || productId } }"
  >
    <div v-if="!horizontalCard" class="product-thumbnail__icon">
      <adn-tag
        v-if="isAuthenticated && isOnDiscount"
        background-color="var(--color-promo)"
        font-size="var(--font-size-xs)"
        font-weight="bold"
        text-color="white"
        shape="0.4rem"
        border-style="none"
        data-test-on-discount
      >
        {{ i18n._('Promotion') }}
      </adn-tag>
      <adn-tag
        v-if="isAuthenticated && isExcluTech"
        background-color="var(--color-coop)"
        font-size="var(--font-size-xs)"
        font-weight="bold"
        text-color="white"
        shape="0.4rem"
        border-style="none"
        data-test-exclu-tech-badge
      >
        {{ i18n._('Exclu') }} {{ partnerType }}
      </adn-tag>
      <adn-tag
        v-if="isInnovation"
        background-color="var(--color-main)"
        font-size="var(--font-size-xs)"
        font-weight="bold"
        text-color="white"
        shape="0.4rem"
        border-style="none"
        data-test-supplier-highlighted
        :class="tagClassInnovation"
      >
        {{ i18n._('Innovation') }}
      </adn-tag>
    </div>
    <div v-if="productLabel" class="product-thumbnail__product-type-container" data-test-label-banner>
      <div class="product-thumbnail__product-category">
        {{ productLabel }}
      </div>
    </div>
    <adn-image
      :source="imageSource"
      cloudinary-options="e_trim,w_auto"
      :alt="productName"
      :class="['product-thumbnail__image', { 'product-thumbnail__image--small': horizontalCard }]"
      data-test="product-image"
    />
    <div class="product-thumbnail__informations">
      <div class="product-thumbnail__description">
        <div
          v-if="!isFarmi && displaySupplierName"
          class="product-thumbnail__supplier-reduce"
          data-test="supplier-or-provider"
        >
          {{ supplierName }}
        </div>
        <h3 class="product-thumbnail__name">
          {{ productNameUpperCase }}
          <span v-if="shouldDisplayCropName" class="product-thumbnail__cropname" data-test="cropname">
            {{ cropName }}
          </span>
        </h3>
        <div v-if="isAuthenticated && !horizontalCard" class="product-thumbnail__badges">
          <div v-if="isHighlighted" class="product-thumbnail__badge product-thumbnail__highlighted">
            <span
              class="product-thumbnail__badge-icon adn-icon adn-icon--category-recommended"
              :aria-label="i18n._('Mis en avant')"
              data-test-highlight-icon
            />
            {{ i18n._('Recommandé') }}
          </div>
        </div>
      </div>
      <div v-if="isReservationAtZero" class="product-thumbnail__no-offer" data-test-no-offer-label>
        {{ i18n._('Disponible à la réservation') }}
      </div>
      <div v-else-if="bestOffer" class="product-thumbnail__price-block">
        <div
          v-if="isOffSeasonProduct"
          class="product-thumbnail__price-from product-thumbnail__price-from-quickfix"
          data-test="off-season-message"
        >
          {{ i18n._('Offre du moment') }}
        </div>
        <p-product-price-from
          :best-offer="bestOffer"
          :is-off-season-product="isOffSeasonProduct"
          data-test="product-price"
        />
      </div>
      <div v-else-if="isAuthenticated" class="product-thumbnail__no-offer" data-test-no-offer-label>
        {{ i18n._('Prix disponible à la demande') }}
      </div>
    </div>
  </router-link>
</template>

<script lang="ts">
import { useProductCard } from '@b2ag/catalog/src/useProductCard'
import { computed, defineComponent } from 'vue'
import { isAladin, isFarmi } from '@b2ag/app-brands'
import { i18n, getAppTypeLocale } from '@b2ag/locale'
import AdnTag from '@b2ag/design-system/src/components/AdnTag/AdnTag.vue'
import { toUpperCaseExceptMicron } from '@b2ag/utils/src/toUppercaseExceptMicron'
import { type AppBrand } from '@b2ag/app-brands'
import { getProductDefaultImageFull } from '../../services/products-images.constants'
import PProductPriceFrom from '../PProductPriceFrom/PProductPriceFrom.vue'
import { currentAppBrand } from '@b2ag/app-brands'
import AdnImage from '@b2ag/design-system/src/components/AdnImage/AdnImage.vue'

export default defineComponent({
  name: 'PProductThumbnail',
  components: { PProductPriceFrom, AdnTag, AdnImage },
  props: {
    productId: {
      type: String,
      required: true,
    },
    productSlug: {
      type: String,
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
    isAnAssociatedProduct: {
      type: Boolean,
      default: false,
    },
    horizontalCard: {
      type: Boolean,
      default: false,
    },
    displaySupplierName: {
      type: Boolean,
      default: true,
    },
    fixedWidth: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const {
      product,
      bestOffer,
      bestOfferPriceType,
      isOffSeasonProduct,
      isOnDiscount,
      isHighlighted,
      isInnovation,
      isExclusive,
      isExcluTech,
    } = useProductCard(props.productId)

    const defaultImage = computed(
      () => getProductDefaultImageFull(product.value?.kind || '', product.value?.type) || '',
    )
    const imageSource = computed(() => product.value?.thumbnail || defaultImage.value)
    const productLabel = computed(() => product.value?.label || '')
    const productName = computed(() => product.value?.name || '')
    const supplierName = computed(() => product.value?.supplier || '')

    // à remplacer plus tard par les variables de thème (les gradients ne sont pas gérés actuellement)
    const tagClassInnovation = computed(() => {
      if (isAladin) {
        return 'tag--innovation-blue'
      }
      if (isFarmi) {
        return 'tag--innovation-green'
      }
      return 'tag--innovation-none'
    })

    const productNameUpperCase = computed(() => toUpperCaseExceptMicron(product.value ? product.value?.name : ''))

    const appTypeLocale = getAppTypeLocale(currentAppBrand as AppBrand)
    const partnerType = computed(() => appTypeLocale.tc)

    const isReservationAtZero = computed<boolean>(() => {
      if (bestOfferPriceType?.value !== 'preorderPrice') return false

      const basePrice = bestOffer.value?.detailPrice.basePrice.valueOf()

      return Boolean(basePrice && basePrice < 0.01)
    })

    const normalizeString = (str: string): string => {
      return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    }
    const shouldDisplayCropName = computed(() => {
      if (!cropName.value) return false
      const normalizedProductName = normalizeString(productName.value)
      const normalizedCropName = normalizeString(cropName.value)
      return normalizedProductName !== normalizedCropName
    })
    const cropName = computed(() => product.value?.crop_name || '')

    return {
      isAladin,
      isFarmi,
      isOffSeasonProduct,
      imageSource,
      supplierName,
      bestOffer,
      productLabel,
      productName,
      productNameUpperCase,
      isOnDiscount,
      isHighlighted,
      isInnovation,
      isExclusive,
      isExcluTech,
      tagClassInnovation,
      partnerType,
      isReservationAtZero,
      shouldDisplayCropName,
      cropName,
      i18n,
    }
  },
})
</script>

<style lang="scss" scoped>
$product-type-height: 2.4rem;
$product-type-skew-x: 25deg;

.authenticated .product-thumbnail__link {
  @media (min-width: $breakpoint-sm) {
    height: 38.4rem;
  }

  &--horizontal {
    height: 14.6rem;
  }
}

.product-thumbnail__link {
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: row;
  gap: var(--spacing-3);
  box-shadow: -0.2rem 0.2rem 0.8rem rgb(87 94 98 / 20%);
  padding: var(--spacing-3);
  background-color: var(--color-white);
  color: var(--color-black);
  justify-content: flex-start;
  border-radius: var(--border-radius-1);
  max-width: 100%;
  min-width: 27rem;
  margin-top: var(--spacing-2);
  align-items: center;

  @media (min-width: $breakpoint-sm) {
    height: 26rem;
    flex-direction: column;
    align-items: center;

    &--fixedWidth {
      width: 21rem;
    }
  }

  &--horizontal {
    flex-direction: row;
    height: 11.6rem;
    width: 25rem;
    margin: var(--spacing-1);

    .product-thumbnail__name {
      height: fit-content;
    }
  }
}

.product-thumbnail__icon {
  display: flex;
  flex-direction: row;
  position: absolute;
  align-items: center;
  top: 0;
  transform: translate(0, -50%);
  left: 0;
  padding: 0 var(--spacing-2);
  width: inherit;

  .adn-icon {
    line-height: 3.2rem;
  }

  div {
    margin-right: 0.4rem;
  }

  div:nth-child(n + 4) {
    display: none;
  }

  .tag {
    padding: 0.2rem 0.8rem;
  }

  .tag--innovation-blue {
    @include apply-linear-gradient((var(--gradient-main-start), var(--gradient-main-end)), 90deg);
  }

  .tag--innovation-green {
    @include apply-linear-gradient((var(--gradient-green-start), var(--gradient-green-end)), 90deg);
  }
}

.product-thumbnail__badges {
  display: flex;
  margin-top: var(--spacing-2);
  margin-bottom: var(--spacing-2);
  gap: var(--spacing-2);
}

.product-thumbnail__badge {
  display: flex;
  align-items: center;
  font-size: var(--font-size-xs);
  font-weight: bold;
  gap: var(--spacing-1);

  &-icon {
    font-size: var(--font-size-l);
  }
}

.product-thumbnail__highlighted {
  color: var(--color-secondary);
}

.product-thumbnail__exclusivity {
  color: var(--color-highlight);
}

.product-thumbnail__on-discount-icon {
  background-color: var(--color-promo);
  padding: 0.2rem var(--spacing-1);
  font-weight: bold;
  color: var(--color-white);
  border-radius: var(--border-radius-1);
  font-size: var(--font-size-s);
  height: var(--line-height-m);
}

.authenticated .product-thumbnail__informations {
  justify-content: space-between;
  flex-grow: 1;
}

.product-thumbnail__informations {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;

  @media (min-width: $breakpoint-sm) {
    height: auto;
  }
}

.product-thumbnail__description {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.product-thumbnail__supplier-reduce {
  font-size: var(--font-size-xs);
  color: var(--color-grey-dark);
  text-transform: uppercase;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.product-thumbnail__name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  line-height: 2.2rem;
  white-space: pre-wrap;
  height: min-content;
  font-size: var(--font-size-m);
  margin-bottom: 0;
  font-weight: 700;
  -webkit-line-clamp: 2; /* number of lines to show */
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: $breakpoint-sm) {
    height: 4.4rem;
  }
}

.product-thumbnail__cropname {
  font-size: var(--font-size-m);
  color: var(--color-grey-dark);
  font-weight: 700;
  line-height: 2.2rem;
}

.product-thumbnail__product-type-container {
  transition: all 800ms;
}

.product-thumbnail__image {
  min-width: 10rem;
  width: 10rem;
  height: 10rem;
  object-fit: contain;

  @media screen and (min-width: $breakpoint-sm) {
    min-width: auto;
    width: 100%;
    height: 16rem;
    object-fit: contain;
  }

  &--small {
    flex: 0 0 100%;
    max-width: 10rem;
    height: 100%;
  }
}

.product-thumbnail__company {
  text-transform: uppercase;
  line-height: 2rem;

  @include text-truncate;
}

.product-thumbnail__price-block {
  line-height: 22px;
  margin-top: var(--spacing-2);
}

.product-thumbnail__price-from {
  padding: 0.2rem 0 0.3rem;
  line-height: 1.8rem;
  color: var(--color-text-light);
}

.product-thumbnail__price-from-quickfix {
  display: none;
}

.product-thumbnail__price-full {
  text-transform: uppercase;
}

.product-thumbnail__price {
  font-size: 2.2rem;
  font-weight: bold;
}

.product-thumbnail__rpd-from {
  color: var(--color-text-light);
}

.product-thumbnail__rpd {
  font-weight: bold;
}

.product-thumbnail__no-offer {
  margin-top: 1rem;
  font-size: 1.4rem;
  line-height: 2rem;
  color: var(--color-text-light);
  width: 10rem;
}

.product-thumbnail__product-type-container {
  position: absolute;
  top: -0.1rem;
  left: -1rem;
  padding: 0 1.5rem 0 2rem;
  transform: skewX(-$product-type-skew-x);
  background-color: var(--color-secondary);
}

.product-thumbnail__product-category {
  line-height: $product-type-height;
  transform: skewX($product-type-skew-x);
  color: var(--color-white);
  font-size: 1.4rem;
  text-align: left;
}
</style>
